import React, { useEffect, useRef, useState, useMemo } from 'react';
import './WindowsFeature.css';

const WindowsAnimation = () => {
  const [activeElement, setActiveElement] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showMouse, setShowMouse] = useState(false);
  const [processOrderClicked, setProcessOrderClicked] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [viewButtonClicked, setViewButtonClicked] = useState(false);
  const [showCrtEffect, setShowCrtEffect] = useState(false);
  const [crtAnimationComplete, setCrtAnimationComplete] = useState(false);
  const chatMessagesRef = useRef(null);
  const containerRef = useRef(null);

  const chatSequence = useMemo(() => [
    { type: 'user', text: 'Can you process order #002?', delay: 500 },
    { type: 'ai', text: 'I\'ll help you process that order. Let me first open the Orders menu to navigate to Order Processing...', delay: 1200 },
    { type: 'ai', text: 'Opening the Orders dropdown menu now. I can see the Order Processing option.', delay: 1000 },
    { type: 'ai', text: 'Now I\'ll navigate to the order data. I can see order #002 with status "Pending". Let me click the View button.', delay: 1800 },
    { type: 'ai', text: 'Processing the order now. A confirmation dialog has appeared asking "Are you sure you want to process this order?"', delay: 1500 },
    { type: 'ai', text: 'I\'ll click "Yes" to confirm the order processing.', delay: 1000 },
    { type: 'success', text: 'Order #002 has been successfully processed!', delay: 800 }
  ], []);

  useEffect(() => {
    const runAnimation = () => {
      let currentMessageIndex = 0;
      let animationTimer;

      // Reset all states
      setChatMessages([]);
      setActiveElement(0);
      setIsTyping(false);
      setShowMouse(false);
      setProcessOrderClicked(false);
      setShowDropdown(false);
      setViewButtonClicked(false);

      const nextStep = () => {
        if (currentMessageIndex < chatSequence.length) {
          const currentMessage = chatSequence[currentMessageIndex];
          
          if (currentMessage.type === 'ai') {
            setIsTyping(true);
            
            // Coordinate mouse movements with AI actions
            if (currentMessageIndex === 1) {
              // AI mentions opening Orders menu - show mouse moving to menu
              setTimeout(() => {
                setShowMouse(true);
                setMousePosition({ x: 32, y: 10 }); // Position over Orders menu item
              }, 500);
            } else if (currentMessageIndex === 2) {
              // AI mentions opening dropdown - show dropdown
              setTimeout(() => {
                setShowDropdown(true);
              }, 300);
            }
            
            animationTimer = setTimeout(() => {
              setIsTyping(false);
              setChatMessages(prev => [...prev, currentMessage]);
              currentMessageIndex++;
              
              // Post-message UI actions
              if (currentMessageIndex === 4) {
                // After mentioning order - hide dropdown and move to order row
                setTimeout(() => {
                  setShowDropdown(false);
                  setActiveElement(2);
                  setMousePosition({ x: 85, y: 52 }); // Position over View button in row #002
                }, 200);
                // Click the View button after mouse reaches it
                setTimeout(() => {
                  setViewButtonClicked(true);
                  // Reset click state after animation
                  setTimeout(() => {
                    setViewButtonClicked(false);
                  }, 300);
                }, 1000);
              } else if (currentMessageIndex === 5) {
                // After mentioning dialog - show confirmation dialog
                setTimeout(() => {
                  setActiveElement(1);
                  setMousePosition({ x: 38, y: 68 }); // Position over Yes button in modal
                }, 300);
              } else if (currentMessageIndex === 6) {
                // After mentioning click - animate button click
                setTimeout(() => {
                  setProcessOrderClicked(true);
                }, 200);
              } else if (currentMessageIndex === 7) {
                // After success - reset everything
                setTimeout(() => {
                  setActiveElement(-1);
                  setShowMouse(false);
                  setShowDropdown(false);
                  setProcessOrderClicked(false);
                  setViewButtonClicked(false);
                }, 500);
              }
              
              animationTimer = setTimeout(nextStep, currentMessage.delay);
            }, 800);
          } else {
            setChatMessages(prev => [...prev, currentMessage]);
            currentMessageIndex++;
            animationTimer = setTimeout(nextStep, currentMessage.delay);
          }
        } else {
          // Restart sequence
          animationTimer = setTimeout(runAnimation, 3000);
        }
      };

      nextStep();
      return () => clearTimeout(animationTimer);
    };

    const cleanup = runAnimation();
    return cleanup;
  }, [chatSequence]);

  // Auto-scroll chat messages to bottom
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [chatMessages, isTyping]);

  // CRT screen effect when component comes into view (desktop only)
  useEffect(() => {
    // Skip CRT effect on mobile
    const isMobile = window.innerWidth <= 768;
    if (isMobile) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Reset and play CRT effect each time component comes into view
            setCrtAnimationComplete(false);
            setShowCrtEffect(true);
            // CRT animation completes after 3.5s (longer effect)
            setTimeout(() => {
              setCrtAnimationComplete(true);
              // Hide overlay after animation
              setTimeout(() => {
                setShowCrtEffect(false);
              }, 400);
            }, 3500);
          } else {
            // Reset when leaving view so it can play again
            setShowCrtEffect(false);
            setCrtAnimationComplete(false);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="animation-container windows-3d-anim" ref={containerRef}>
      {/* CRT Screen Power-On Overlay */}
      {showCrtEffect && (
        <div className={`crt-overlay ${crtAnimationComplete ? 'fade-out' : ''}`}>
          <div className="crt-scanlines"></div>
          <div className="crt-flicker"></div>
          <div className="crt-vignette"></div>
          <div className="crt-glow"></div>
          <div className="crt-horizontal-line"></div>
          <div className="crt-branding">
            <img src="/qatelogo.png" alt="Qate" className="crt-logo" loading="lazy" />
            <div className="crt-message">Taking control over desktop...</div>
          </div>
        </div>
      )}
      <div className="windows-app-container">
        {/* 3D Windows Application */}
        <div className="windows-app-3d">
          {/* Animated Mouse Cursor */}
          {showMouse && (
            <div 
              className="animated-mouse-cursor" 
              style={{
                left: `${mousePosition.x}%`,
                top: `${mousePosition.y}%`
              }}
            >
              <div className="mouse-pointer">
                <svg width="40" height="40" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Modern cursor with subtle shadow and clean design */}
                  <defs>
                    <linearGradient id="cursorGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#ffffff" stopOpacity="1"/>
                      <stop offset="100%" stopColor="#f8fafc" stopOpacity="1"/>
                    </linearGradient>
                    <filter id="cursorShadow" x="-50%" y="-50%" width="200%" height="200%">
                      <feDropShadow dx="1" dy="2" stdDeviation="2" floodColor="#000000" floodOpacity="0.25"/>
                    </filter>
                  </defs>
                  {/* Cursor body */}
                  <path 
                    d="M2 2 L2 16 L6.5 12.5 L9 18 L11.5 17 L9 11.5 L15 11.5 L2 2 Z" 
                    fill="url(#cursorGradient)" 
                    stroke="#1f2937" 
                    strokeWidth="0.5" 
                    strokeLinejoin="round"
                    filter="url(#cursorShadow)"
                  />
                </svg>
              </div>
              <div className="mouse-click-ripple"></div>
            </div>
          )}
          
          {/* Window Frame */}
          <div className="window-frame">
            {/* Title Bar */}
            <div className="title-bar">
              <div className="title-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M3 9h18M9 21V9" />
                </svg>
              </div>
              <div className="title-text">Legacy Enterprise App</div>
              <div className="title-bar-controls">
                <div className="window-control minimize"></div>
                <div className="window-control maximize"></div>
                <div className="window-control close"></div>
              </div>
            </div>
            
            {/* Menu Bar */}
            <div className={`menu-bar ${activeElement === 0 ? 'testing' : ''}`}>
              <div className="menu-item">File</div>
              <div className="menu-item">Edit</div>
              <div className="menu-item">View</div>
              <div className={`menu-item dropdown-menu ${showDropdown ? 'active' : ''}`}>
                Orders
                {showDropdown && (
                  <div className="dropdown-content">
                    <div className="dropdown-item">New Order</div>
                    <div className="dropdown-item highlighted">Order Processing</div>
                    <div className="dropdown-item">Order History</div>
                    <div className="dropdown-separator"></div>
                    <div className="dropdown-item">Reports</div>
                  </div>
                )}
              </div>
              <div className="menu-item">Tools</div>
              <div className="menu-item">Help</div>
            </div>
            
            {/* Main Content Area */}
            <div className="app-content">
              {/* Toolbar */}
              <div className="toolbar">
                <div className="toolbar-group">
                  <div className="toolbar-button">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
                    </svg>
                  </div>
                  <div className="toolbar-button">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                      <polyline points="17 21 17 13 7 13 7 21" />
                      <polyline points="7 3 7 8 15 8" />
                    </svg>
                  </div>
                  <div className="toolbar-button">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="6" cy="6" r="3" />
                      <circle cx="6" cy="18" r="3" />
                      <line x1="20" y1="4" x2="8.12" y2="15.88" />
                      <line x1="14.47" y1="14.48" x2="20" y2="20" />
                      <line x1="8.12" y1="8.12" x2="12" y2="12" />
                    </svg>
                  </div>
                  <div className="toolbar-button">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" />
                      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                    </svg>
                  </div>
                </div>
                <div className="toolbar-separator"></div>
                <div className="toolbar-group">
                  <div className="toolbar-button">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </div>
                  <div className="toolbar-button">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
                    </svg>
                  </div>
                </div>
              </div>
              
              {/* Data Grid */}
              <div className="data-grid">
                <div className="grid-header">
                  <div className="grid-cell header">Order ID</div>
                  <div className="grid-cell header">Customer</div>
                  <div className="grid-cell header">Status</div>
                  <div className="grid-cell header">Actions</div>
                </div>
                <div className="grid-row">
                  <div className="grid-cell">#001</div>
                  <div className="grid-cell">John Smith</div>
                  <div className="grid-cell status-active">Completed</div>
                  <div className="grid-cell">
                    <div className="grid-button">View</div>
                  </div>
                </div>
                <div className={`grid-row ${activeElement === 2 ? 'highlighted-row' : ''}`}>
                  <div className="grid-cell">#002</div>
                  <div className="grid-cell">Sarah Johnson</div>
                  <div className="grid-cell status-pending">Pending</div>
                  <div className="grid-cell">
                    <div className={`grid-button ${activeElement === 2 ? 'testing' : ''} ${viewButtonClicked ? 'clicked' : ''}`}>View</div>
                  </div>
                </div>
                <div className="grid-row">
                  <div className="grid-cell">#003</div>
                  <div className="grid-cell">Mike Davis</div>
                  <div className="grid-cell status-active">Shipped</div>
                  <div className="grid-cell">
                    <div className="grid-button">View</div>
                  </div>
                </div>
                <div className="grid-row">
                  <div className="grid-cell">#004</div>
                  <div className="grid-cell">Lisa Brown</div>
                  <div className="grid-cell status-pending">Processing</div>
                  <div className="grid-cell">
                    <div className="grid-button">View</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Status Bar */}
            <div className={`status-bar ${activeElement === 3 ? 'testing' : ''}`}>
              <div className="status-item">Ready</div>
              <div className="status-separator">|</div>
              <div className="status-item">Records: 1,247</div>
              <div className="status-separator">|</div>
              <div className="status-item">Connected</div>
              <div className="status-progress">
                <div className="progress-bar"></div>
              </div>
            </div>
          </div>
          
          {/* Modal Dialog */}
          <div className={`modal-dialog ${activeElement === 1 ? 'visible testing' : ''}`}>
            <div className="modal-title-bar">
              <span className="modal-title">Confirm Action</span>
              <div className="modal-close">×</div>
            </div>
            <div className="modal-content">
              <div className="modal-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div className="modal-text">
                Are you sure you want to process this order?
              </div>
              <div className="modal-buttons">
                <div className={`modal-button primary ${processOrderClicked ? 'clicked' : ''}`}>Yes</div>
                <div className="modal-button secondary">Cancel</div>
              </div>
            </div>
          </div>
          
          {/* Testing Indicator */}
          <div className="test-indicator">
            <div className="test-pulse"></div>
            <div className="test-label">AI using the Desktop</div>
          </div>
        </div>
        
        {/* AI Agent Chat Interface */}
        <div className="agent-chat-window">
          <div className="chat-window-header">QATE
            <div className="chat-window-title">
              <div className="agent-status-indicator">
                <div className="status-dot active"></div>
                <span>Active</span>
              </div>
            </div>
          </div>
          
          <div className="chat-messages-container" ref={chatMessagesRef}>
            {chatMessages.map((message, index) => (
              <div key={index} className={`chat-message ${message.type}`}>
                <div className="message-avatar">
                  {message.type === 'user' ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  ) : message.type === 'success' ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="10" rx="2" />
                      <circle cx="12" cy="5" r="2" />
                      <path d="M12 7v4" />
                    </svg>
                  )}
                </div>
                <div className="message-content">
                  <div className="message-text">{message.text}</div>
                  <div className="message-timestamp">
                    {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="chat-message ai">
                <div className="message-avatar">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="10" rx="2" />
                    <circle cx="12" cy="5" r="2" />
                    <path d="M12 7v4" />
                  </svg>
                </div>
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="chat-input-area">
            <div className="chat-input-box">
              <input type="text" placeholder="Ask me to test anything..." disabled />
              <button className="send-button">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        
        {/* Shadow and depth */}
        <div className="app-shadow"></div>
      </div>
    </div>
  );
};

const WindowsFeature = {
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="4" width="20" height="12" rx="2"></rect>
      <path d="M8 16h8M12 12v4"></path>
      <circle cx="8" cy="8" r="1"></circle>
      <circle cx="16" cy="8" r="1"></circle>
    </svg>
  ),
  title: <><span className="title-prefix">Web, REST, SOAP — and </span>Windows Desktop Testing</>,
  description: "The only AI platform that truly tests native Windows desktop applications. A native C# Windows service installs directly on your machines for pixel-perfect automation of the legacy software other tools can't reach — so you finally get comprehensive coverage across your whole stack.",
  highlight: ["Industry First", "Pro Plan"],
  animation: <WindowsAnimation />,
  features: [
    {
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="16 18 22 12 16 6" />
          <polyline points="8 6 2 12 8 18" />
        </svg>
      ),
      text: "<strong>Native Windows service</strong> - installs directly on your machines"
    },
    {
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      ),
      text: "<strong>Legacy app support</strong> from Windows 95 to Windows 11"
    },
    {
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <circle cx="12" cy="12" r="6" />
          <circle cx="12" cy="12" r="2" />
        </svg>
      ),
      text: "<strong>Agent UI automation</strong> that other platforms cannot do"
    }
  ]
};

export default WindowsFeature;